import { useState } from 'react'
import { Dialog, Button, Typography, Checkbox, Box, Paper, useTheme, useMediaQuery, alpha } from '@mui/material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Trans } from '@lingui/macro'

interface AIConsentDialogProps {
  open: boolean
  onAccept: () => void
}

const CONSENT_STORAGE_KEY = 'medconnect-agent-ai-consent'

export function hasAIConsent(): boolean {
  return localStorage.getItem(CONSENT_STORAGE_KEY) === 'true'
}

export function setAIConsent(): void {
  localStorage.setItem(CONSENT_STORAGE_KEY, 'true')
}

const INFO_CARDS = [
  {
    icon: SmartToyIcon,
    labelTrans: <Trans>TECHNOLOGY</Trans>,
    text: <Trans>You are interacting with an artificial intelligence</Trans>,
    color: 'primary' as const,
    border: false,
  },
  {
    icon: AutoAwesomeIcon,
    labelTrans: <Trans>HOW IT WORKS</Trans>,
    text: <Trans>Responses are generated automatically</Trans>,
    color: 'primary' as const,
    border: false,
  },
  {
    icon: WarningAmberIcon,
    labelTrans: <Trans>VIGILANCE</Trans>,
    text: <Trans>AI can make errors or hallucinate facts</Trans>,
    color: 'warning' as const,
    border: false,
  },
  {
    icon: AssignmentIcon,
    labelTrans: <Trans>EXPERT</Trans>,
    text: <Trans>Keep your clinical judgment at all times</Trans>,
    color: 'primary' as const,
    border: true,
  },
]

export function AIConsentDialog({ open, onAccept }: AIConsentDialogProps) {
  const [checked, setChecked] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleAccept = () => {
    setAIConsent()
    onAccept()
  }

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'auto',
          boxShadow: isMobile ? 'none' : '0 12px 40px rgba(25,28,29,0.06)',
        },
      }}
    >
      {/* Hero gradient */}
      <Box
        sx={{
          height: { xs: 140, sm: 160 },
          minHeight: { xs: 140, sm: 160 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent 60%)`,
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 2.5, sm: 5 }, pb: { xs: 3, sm: 5 }, mt: -6, position: 'relative', zIndex: 1 }}>
        {/* Title */}
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} sx={{ mb: 1, letterSpacing: '-0.02em' }}>
          <Trans>AI Assistant Information</Trans>
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          <Trans>This tool uses advanced technology to assist you. Before starting, please review this important information.</Trans>
        </Typography>

        {/* Info cards — 1 col on mobile, 2 on desktop */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          {INFO_CARDS.map((card, i) => {
            const Icon = card.icon
            const cardColor = card.color === 'warning' ? theme.palette.warning.main : theme.palette.primary.main
            return (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[100], 0.7),
                  ...(card.border && {
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                  }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Icon sx={{ color: cardColor, fontSize: 20 }} />
                  <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.1em', color: cardColor, lineHeight: 1 }}>
                    {card.labelTrans}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: card.border ? 600 : 400 }}>
                  {card.text}
                </Typography>
              </Paper>
            )
          })}
        </Box>

        {/* Consent checkbox */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            mb: 3,
          }}
        >
          <Box component="label" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, cursor: 'pointer' }}>
            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} sx={{ mt: -0.5 }} />
            <Typography variant="body1" color="text.secondary">
              <Trans>I understand that I am interacting with an AI solution and I accept the terms of use</Trans>
            </Typography>
          </Box>
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column-reverse',
            sm: { flexDirection: 'row' },
            alignItems: { xs: 'stretch', sm: 'flex-end' },
            justifyContent: 'flex-end',
            gap: 2,
            pt: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="contained"
            onClick={handleAccept}
            disabled={!checked}
            size="large"
            fullWidth={isMobile}
            endIcon={<ArrowForwardIcon />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 600,
            }}
          >
            <Trans>Accept and continue</Trans>
          </Button>
        </Box>
      </Box>
    </Dialog>
  )
}
